/**
 * Brand Kit Zod Schemas
 * Validation schemas and inferred types for brand kit forms
 */

import { z } from "zod";

export const MAX_ADDITIONAL_TEXT_LENGTH = 50_000;
export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_NAME_LENGTH = 200;
export const MAX_DOCUMENTS = 5;
export const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

export const ALLOWED_DOCUMENT_TYPES = ["application/pdf"];

const BRAND_DESCRIPTION_URL_ERROR_MESSAGE =
  "Please enter a complete URL (e.g., https://example.com)";

function isValidBrandDescriptionUrl(value: string): boolean {
  // Reject malformed protocol separators that the URL constructor would
  // otherwise normalize into a valid-looking URL.
  if (!/^https?:\/\//i.test(value) || value.includes("\\")) {
    return false;
  }

  try {
    const url = new URL(value);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return false;
    }

    const hostname = url.hostname;
    const hostnameParts = hostname.split(".");

    // Accept IPv4 hostnames.
    const isIpv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    if (isIpv4) {
      return hostnameParts.every((part) => {
        const octet = Number(part);
        return Number.isInteger(octet) && octet >= 0 && octet <= 255;
      });
    }

    // Require at least one dot and a 2+ char TLD for normal domains.
    if (hostnameParts.length < 2) {
      return false;
    }
    const tld = hostnameParts.at(-1);
    if (!tld) {
      return false;
    }
    return /^[a-z]{2,}$/i.test(tld);
  } catch {
    return false;
  }
}

// Tone Input Schemas (for response/display - read-only from API)
export const toneInputWebsiteUrlSchema = z.object({
  type: z.literal("websiteUrl"),
  url: z.string(),
});

export const toneInputBlogUrlSchema = z.object({
  type: z.literal("blogUrl"),
  url: z.string(),
});

export const toneInputDocumentSchema = z.object({
  type: z.literal("document"),
  filename: z.string().optional(),
});

export const toneInputRawTextSchema = z.object({
  type: z.literal("rawText"),
  textContent: z.string(),
});

export const toneInputSchema = z.discriminatedUnion("type", [
  toneInputWebsiteUrlSchema,
  toneInputBlogUrlSchema,
  toneInputDocumentSchema,
  toneInputRawTextSchema,
]);

// Tone Profile Form Schema (for new multipart/form-data API)
// referenceUrls uses object format for react-hook-form field array compatibility
export const toneProfileFormSchema = z.object({
  name: z
    .string()
    .min(1, "Profile name is required")
    .max(MAX_NAME_LENGTH, `Maximum ${MAX_NAME_LENGTH} characters`),
  description: z
    .string()
    .max(MAX_DESCRIPTION_LENGTH, `Maximum ${MAX_DESCRIPTION_LENGTH} characters`)
    .optional()
    .default(""),
  referenceUrls: z
    .array(
      z.object({
        value: z.string().url("Please enter a valid URL").or(z.literal("")),
      })
    )
    .optional()
    .default([]),
  additionalText: z
    .string()
    .max(
      MAX_ADDITIONAL_TEXT_LENGTH,
      `Maximum ${MAX_ADDITIONAL_TEXT_LENGTH.toLocaleString()} characters`
    )
    .optional()
    .default(""),
  // Documents are validated separately in the form component since File objects
  // don't serialize well through zod
});

// Brand Description Form Schema
export const brandDescriptionUrlSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1, BRAND_DESCRIPTION_URL_ERROR_MESSAGE)
    .refine(isValidBrandDescriptionUrl, {
      message: BRAND_DESCRIPTION_URL_ERROR_MESSAGE,
    }),
});

export const brandDescriptionFormSchema = z.object({
  urls: z
    .array(brandDescriptionUrlSchema)
    .min(1, "At least one URL is required"),
});

// Brand Description Edit Schema (for manual editing)
export const brandDescriptionEditSchema = z.object({
  value: z
    .string()
    .min(1, "Brand description cannot be empty")
    .max(
      MAX_DESCRIPTION_LENGTH,
      `Maximum ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters`
    )
    .refine((val) => val.trim().length > 0, {
      message: "Brand description cannot be empty",
    }),
});

// Custom Instructions Schemas
export const MAX_CUSTOM_INSTRUCTIONS_LENGTH = 5000;
export const MAX_BRIEF_LENGTH = 10_000;
export const MAX_HASHTAGS_LENGTH = 2000;

// Schema for individual instruction field (used when editing a single field)
export const customInstructionFieldSchema = z.object({
  value: z.string().max(MAX_CUSTOM_INSTRUCTIONS_LENGTH, {
    message: `Maximum ${MAX_CUSTOM_INSTRUCTIONS_LENGTH.toLocaleString()} characters`,
  }),
});

// Schema for Copy Brief - cannot be empty/whitespace
export const copyBriefFieldSchema = z.object({
  value: z
    .string()
    .min(1, "Copy Brief cannot be empty")
    .max(MAX_BRIEF_LENGTH, {
      message: `Maximum ${MAX_BRIEF_LENGTH.toLocaleString()} characters`,
    })
    .refine((val) => val.trim().length > 0, {
      message: "Copy Brief cannot be empty",
    }),
});

// Schema for Creative Brief - cannot be empty/whitespace
export const creativeBriefFieldSchema = z.object({
  value: z
    .string()
    .min(1, "Creative Brief cannot be empty")
    .max(MAX_BRIEF_LENGTH, {
      message: `Maximum ${MAX_BRIEF_LENGTH.toLocaleString()} characters`,
    })
    .refine((val) => val.trim().length > 0, {
      message: "Creative Brief cannot be empty",
    }),
});

// Schema for hashtags field
export const hashtagsFieldSchema = z.object({
  value: z.string().max(MAX_HASHTAGS_LENGTH, {
    message: `Maximum ${MAX_HASHTAGS_LENGTH.toLocaleString()} characters`,
  }),
});

// Inferred Types
export type ToneInputWebsiteUrl = z.infer<typeof toneInputWebsiteUrlSchema>;
export type ToneInputBlogUrl = z.infer<typeof toneInputBlogUrlSchema>;
export type ToneInputDocument = z.infer<typeof toneInputDocumentSchema>;
export type ToneInputRawText = z.infer<typeof toneInputRawTextSchema>;
export type ToneInput = z.infer<typeof toneInputSchema>;
export type ToneProfileFormData = z.infer<typeof toneProfileFormSchema>;
export type BrandDescriptionFormData = z.infer<
  typeof brandDescriptionFormSchema
>;
export type CustomInstructionFieldData = z.infer<
  typeof customInstructionFieldSchema
>;
export type CopyBriefFieldData = z.infer<typeof copyBriefFieldSchema>;
export type CreativeBriefFieldData = z.infer<typeof creativeBriefFieldSchema>;
export type HashtagsFieldData = z.infer<typeof hashtagsFieldSchema>;
export type BrandDescriptionEditData = z.infer<
  typeof brandDescriptionEditSchema
>;
